import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Icon } from '@components';
import { useNavigation, useTheme, useRoute } from '@react-navigation/native';
import { Theme } from '@theme';
import { Contact, useGetCategoryById, usePaginateCategoryContacts, useScreen } from '@hooks';
import { SafeAreaView } from 'react-native-safe-area-context';
import { defaultImage } from '@assets';
import { useLanguage } from '@context';
import { useLoadMoreOnScroll } from '../components/useLoadMoreOnScroll';

type CategoryDetailsProps = {
  categoryId: string;
  onEdit: () => void;
};

const CategoryDetails: React.FC<CategoryDetailsProps> = ({ categoryId: propCategoryId, onEdit }) => {
  const navigation = useNavigation();
  const route = useRoute<any>();
  const theme = useTheme();
  const styles = getStyle(theme);
  const {isMobile} = useScreen();
  const { t } = useLanguage();

  const categoryId = propCategoryId || route?.params?.categoryId;
  
  const { data: category, isFetched } = useGetCategoryById(categoryId);
  const {
    flatData: categoryContacts,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = usePaginateCategoryContacts(categoryId);
  // Only the first page of people was ever asked for: the following ones are
  // loaded while scrolling, and by themselves while the names do not fill the panel.
  const { onScroll, onListLayout, onContentLayout } = useLoadMoreOnScroll({
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  });

  if (!category) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          {!isFetched && <ActivityIndicator size="large" color={theme.colors.primary} />}
          {isFetched && <Text style={styles.errorText}>{t('categories.notFound')}</Text>}
        </View>
      </SafeAreaView>
    );
  }

  const handleEditCategory = () => {
    if(onEdit) {
      onEdit();
    } else {
      (navigation as any).navigate('EditCategory', { categoryId: category!.id });
    }
  };

  const renderContactItem = (contact: Contact) => (
    <TouchableOpacity key={contact.id} style={styles.contactItem} onPress={() => (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.id }
    })}>
      <Image source={!contact.avatarUrl? defaultImage : { uri: contact.avatarUrl}} style={styles.contactAvatar} />
      <Text style={styles.contactName}>{contact.fullName}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {isMobile && <TouchableOpacity
            onPress={() => navigation.goBack()}
          >
            <Icon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>}

          <TouchableOpacity testID="category-detail-photo" style={styles.headerImageContainer} onPress={handleEditCategory}>
            <Image source={!category?.avatarUrl? defaultImage : { uri: category.avatarUrl}} style={styles.categoryPhoto} />
          </TouchableOpacity>

          <Text style={styles.categoryTitle} numberOfLines={1}>{category?.name}</Text>
        </View>

        <TouchableOpacity testID="cat-edit-pencil" onPress={handleEditCategory}>
          <Icon name="edit" size={20} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>

      <Text style={styles.sectionTitle}>{category?.contactAccountIds?.length} {category?.contactAccountIds?.length === 1 ? t('categories.person') : t('categories.persons')}</Text>
      <ScrollView
        testID="category-detail-people"
        style={styles.contactsList}
        showsVerticalScrollIndicator={!isMobile}
        onScroll={onScroll}
        scrollEventThrottle={100}
        onLayout={onListLayout}
      >
        {/* No style on this View: it only measures the height of the rows. */}
        <View onLayout={onContentLayout}>
          {categoryContacts.map(renderContactItem)}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    headerImageContainer: {
      width: 44,
      height: 44,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.spacing.md,
    },
    categoryTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: 'bold',
      color: theme.colors.text,
    },
    sectionTitle: {
      fontSize: 13,
      color: '#8E8E93',
      fontWeight: '400',
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 8,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    errorText: {
      fontSize: 16,
      color: theme.colors.error,
    },
    categoryPhoto: {
      width: 44,
      height: 44,
      borderRadius: 22,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.grey20,
    },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      marginRight: 12,
    },
    contactName: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: '500',
    },
    contactsList: {
      flex: 1,
      //maxHeight: 300,
      marginBottom: 20,
    },
  }); 

export default CategoryDetails 